import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import EventBooking from '../../components/EventBooking';
import { setEventNotify, deleteEventNotify } from './EventBookingAPI';
import eventTypes from '../../constants/eventTypes';
import { selectIsAuthenticated } from '../../reducers/authTokenSlice';
import { DATE_TIME_FORMAT } from '../../constants/dateFormats';

import './EventBookingContainer.scss';
import { getPageContext } from '../../utils';
import { setReservation } from '../../reducers';
import { isWidget } from '../../constants/appContext';
import { getURLSearchParams, setSearchParams } from '../../utils/searchParamUtils';
import { findSlotFromSlots } from '../../utils/bookingHelpers';

const { GENERAL_ADMISSION, SEATED } = eventTypes;
let loginThroughGatedSlotClickTimer = null;

class EventBookingContainer extends React.Component {
  static displayName = 'EventBookingContainer';

  static propTypes = {
    $rootScope: PropTypes.shape({
      $broadcast: PropTypes.func.isRequired,
    }).isRequired,
    Analytics: PropTypes.shape({
      event: PropTypes.func,
    }).isRequired,
    ccRequired: PropTypes.bool.isRequired,
    city: PropTypes.string.isRequired,
    context: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    event: PropTypes.shape({
      hasNotify: PropTypes.bool,
      maxPartySize: PropTypes.number,
      notifyDate: PropTypes.string,
      notifyEndTime: PropTypes.string,
      notifyStartTime: PropTypes.string,
      saleHasEnded: PropTypes.bool,
      serviceTypeId: PropTypes.number,
      title: PropTypes.string,
      venue: PropTypes.string,
      location: PropTypes.shape({
        city: PropTypes.string,
      }),
    }),
    eventSlug: PropTypes.string.isRequired,
    fetchEventDetails: PropTypes.func.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    login: PropTypes.func,
    notifyService: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onTicketQuantitySelect: PropTypes.func.isRequired,
    enableBookNow: PropTypes.bool.isRequired,
    previewMode: PropTypes.bool,
    searchSessionId: PropTypes.string,
    seats: PropTypes.number,
    selectResy: PropTypes.func.isRequired,
    showNotify: PropTypes.bool.isRequired,
    taxIncluded: PropTypes.bool,
    ticketTypes: PropTypes.arrayOf(PropTypes.shape({
      availableQuantity: PropTypes.number,
      description: PropTypes.string,
      isGda: PropTypes.bool,
      isPresaleSoldOut: PropTypes.bool,
      price: PropTypes.string,
      priceNumber: PropTypes.number,
      slots: PropTypes.arrayOf(PropTypes.shape({})),
      soldOut: PropTypes.bool,
      title: PropTypes.string,
    })),
    type: PropTypes.oneOf([GENERAL_ADMISSION, SEATED]),
    userIsGda: PropTypes.bool,
    venueId: PropTypes.number.isRequired,
    venueSlug: PropTypes.string.isRequired,
    eventLargePartyMessage: PropTypes.string,
    venueIsGda: PropTypes.bool,
    setGatingError: PropTypes.func.isRequired,
  }

  static defaultProps = {
    event: {},
    notifyService: null,
    previewMode: false,
    searchSessionId: '',
    seats: null,
    taxIncluded: false,
    ticketTypes: [],
    type: SEATED,
    userIsGda: false,
    login: null,
    eventLargePartyMessage: null,
    venueIsGda: false,
  };

  componentDidMount() {
    const searchParams = getURLSearchParams();
    const tokenParam = searchParams.get('token');
    const exclusiveIdParam = searchParams.get('exclusiveId');
    const scrollY = searchParams.get('scrollY');

    const { ticketTypes } = this.props;
    const clearTokenAndExclusiveId = () => {
      setSearchParams('token', null);
      setSearchParams('exclusiveId', null);
      setSearchParams('scrollY', null);
    };

    const loginThroughGatedSlotClick = (reservations, token, exclusiveId) => {
      const slotFound = findSlotFromSlots(reservations, token, Number(exclusiveId));
      if (slotFound) {
        window.scrollTo(0, scrollY);
        loginThroughGatedSlotClickTimer = setTimeout(() => {
          this.selectResy(slotFound, null, null, null);
          clearTokenAndExclusiveId();
        }, 1000);
      } else {
        loginThroughGatedSlotClickTimer = setTimeout(() => {
          clearTokenAndExclusiveId();
        }, 1000);
      }
    };

    if (ticketTypes && tokenParam) {
      loginThroughGatedSlotClick(ticketTypes, tokenParam, exclusiveIdParam);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      notifyService: prevNotifyService,
    } = prevProps;
    const { notifyService } = this.props;

    if (+notifyService && notifyService !== prevNotifyService) {
      this.placeNotify(+notifyService);
    }
  }

  componentWillUnmount() {
    clearTimeout(loginThroughGatedSlotClickTimer);
  }

  placeNotify = async (notifyService) => {
    const {
      Analytics,
      ccRequired,
      event,
      fetchEventDetails,
      searchSessionId,
      seats,
      venueId,
      venueIsGda,
    } = this.props;
    // Using try so amplitude event is hopefully only called on success
    try {
      const notifyEventProperties = {
        'cc required': ccRequired,
        city: event.location?.city,
        context: getPageContext(window.location.pathname),
        event: event.title,
        'is event': true,
        'party size': seats,
        ...(event.notifyDate && event.notifyStartTime && { time: moment(`${event.notifyDate} ${event.notifyStartTime}`).format(DATE_TIME_FORMAT) }),
        venue: event.venue,
        'venue is GDA': venueIsGda,
      };
      const notifyParams = {
        venue_id: venueId,
        day: event.notifyDate,
        num_seats: seats,
        service_type_id: notifyService,
      };
      let eventName = 'set notify';
      if (event.hasNotify) {
        eventName = 'remove notify';
        await deleteEventNotify(notifyParams);
      } else if (!event.hasNotify) {
        notifyEventProperties['lead time'] = moment(event.notifyDate).diff(moment().startOf('day'), 'days');
        notifyEventProperties.searchSessionId = searchSessionId;
        await setEventNotify({
          ...notifyParams,
          time_preferred_start: event.notifyStartTime,
          time_preferred_end: event.notifyEndTime,
        });
      }

      Analytics.event(eventName, notifyEventProperties);
    } finally {
      fetchEventDetails(seats);
    }
  };

  setNotify = (e, notifyService) => {
    const { $rootScope, isAuthenticated } = this.props;
    if (!isAuthenticated) {
      const successCallback = () => this.placeNotify(notifyService);
      $rootScope.$broadcast('MODAL', 'signin', { successCallback });
    } else {
      this.placeNotify(notifyService);
    }
  }

  selectResy = (reservation, notifyService) => {
    const { seats } = this.props;
    const {
      venueId,
      city,
      date,
      event,
      eventSlug,
      previewMode,
      selectResy,
      venueSlug,
      $rootScope,
      isAuthenticated,
      login,
      context,
      setGatingError,
    } = this.props;

    const reservationConfig = {
      venueId,
      date: moment(date).format('YYYY-MM-DD'),
      seats,
      widgetPath: `cities/${city}/venues/${venueSlug}/events/${eventSlug}`,
      maxPartySize: event.maxPartySize,
    };

    if (notifyService) {
      reservationConfig.notifyService = notifyService;
    }

    if (reservation) {
      reservationConfig.tableConfigId = reservation.token;
    }

    if (!isWidget(context) && !reservation.isEligible) {
      if (isAuthenticated) {
        setGatingError({ ...reservation, venueName: event.venue });
        $rootScope.$broadcast('MODAL', 'gating-error');
      } else {
        setSearchParams('token', reservation.token);
        setSearchParams('exclusiveId', reservation.exclusiveId);
        setSearchParams('scrollY', window.scrollY);
        login();
      }
    } else {
      selectResy({
        reservationConfig,
        reservation,
        fromOutside: true,
        previewMode,
      });
    }
  };

  render() {
    const {
      Analytics,
      context,
      event,
      login,
      onTicketQuantitySelect,
      previewMode,
      seats,
      showNotify,
      taxIncluded,
      ticketTypes,
      type,
      userIsGda,
      enableBookNow,
      eventLargePartyMessage,
    } = this.props;

    return (
      <EventBooking
        Analytics={Analytics}
        context={context}
        event={event}
        onTicketQuantitySelect={onTicketQuantitySelect}
        enableBookNow={enableBookNow}
        previewMode={previewMode}
        selectNotify={this.setNotify}
        selectResy={this.selectResy}
        selectedQuantity={seats}
        showNotify={showNotify}
        taxIncluded={taxIncluded}
        ticketTypes={ticketTypes}
        type={type}
        userIsGda={userIsGda}
        login={login}
        eventLargePartyMessage={eventLargePartyMessage}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  isAuthenticated: selectIsAuthenticated(state),
  searchSessionId: state?.venueSearch?.metadata?.searchSessionId,
});

const mapDispatchToProps = (dispatch) => ({
  setGatingError: (data) => dispatch(setReservation(data)),
});

export { EventBookingContainer as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(EventBookingContainer);
